import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { authReducer } from './slices';
import { baseApi } from './api/baseApi';
import bookingFormReducer from './slices/bookingFormSlice';
import { notificationsReducer } from './slices/notificationsSlice';
import { plannerUiReducer } from './slices/plannerUiSlice';
import { calendarUiReducer } from './slices/calendarUiSlice';
import { requestActionsReducer } from './slices/requestActionsSlice';
import { telemetryMiddleware } from './telemetryMiddleware';

export {
  setUser,
  clearUser,
  setAuthError,
  clearAuthError,
  setAuthLoading,
  updateLastActivity,
  extendSession,
  selectIsAuthenticated,
  selectIsAuthLoading,
  selectUser,
  selectAuthError,
  selectSessionExpiresAt,
  selectLastActivity,
  SESSION_CONFIG,
} from './slices';

export {
  createDraft,
  loadDraft,
  updateStepData,
  commitStep,
  selectSpace,
  goToStep,
  saveAsDraft,
  discardDraft,
  submitForm,
  setEventId,
  setEtag,
  setRequestId,
  setAvailableSpaces,
  setSpacesLoading,
  setApiError,
  setSubmissionStatus,
  setValidationErrors,
  clearValidationErrors,
  selectActiveDraft,
  selectEventId,
  selectEtag,
  selectSelectedSpace,
  selectAvailableSpaces,
  selectSpacesLoading,
  selectCurrentStepIndex,
  selectHighestVisitedStep,
  selectFormData,
  selectIsStepDirty,
  selectStepValidationErrors,
  selectSubmissionStatus,
  selectHasUnsavedChanges,
  selectApiError,
} from './slices/bookingFormSlice';

export const store = configureStore({
  reducer: {
    auth: authReducer,
    bookingForm: bookingFormReducer,
    notifications: notificationsReducer,
    plannerUi: plannerUiReducer,
    calendarUi: calendarUiReducer,
    requestActions: requestActionsReducer,
    [baseApi.reducerPath]: baseApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [],
      },
    }).concat(baseApi.middleware, telemetryMiddleware),
  devTools: import.meta.env.VITE_ENV_NAME !== 'prod',
});

// Enable refetchOnFocus / refetchOnReconnect behavior on RTK Query hooks.
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
